import { Dropdown, theme } from 'aimo-ui';
import {
  CheckIcon,
  DropdownIcon,
  EmptyIcon,
  VEHICLE_INPUT_ICON_SIZE,
} from 'components/account/vehicle/VehicleIcons';
import Icon from 'components/icons/Icon';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EvseConnectorType, Vehicle } from 'types/generatedSchemaTypes';
import { getConnectorIcon } from 'utils/chargingUtils';
import { vehicleChargerPlugTypes } from 'utils/vehicleUtils';

type VehicleChargerPlugTypeSelectorProps = {
  selectedChargerPlugType?: string | null;
  setSelectedChargerPlugType: (values: Partial<Vehicle>) => void;
};

type IconComponentProps = {
  vehicleChargerPlugType: EvseConnectorType;
};

const IconComponent = ({ vehicleChargerPlugType }: IconComponentProps) => {
  const iconName = getConnectorIcon(vehicleChargerPlugType);
  if (iconName === '') {
    return <EmptyIcon />;
  }
  return (
    <Icon
      name={getConnectorIcon(vehicleChargerPlugType)}
      size={VEHICLE_INPUT_ICON_SIZE}
      strokeColor={theme.colors.black}
      color={theme.colors.white}
      testID={`${vehicleChargerPlugType}-icon`}
    />
  );
};

const VehicleChargerPlugTypeSelector = ({
  selectedChargerPlugType,
  setSelectedChargerPlugType,
}: VehicleChargerPlugTypeSelectorProps) => {
  const { t } = useTranslation();
  const vehicleChargerPlugTypesDropdownItems = useMemo(
    () =>
      vehicleChargerPlugTypes
        .map((vehicleChargerPlugType) => {
          return {
            label: t(
              `evCharging.connectorSupportedTypes.${vehicleChargerPlugType}`
            ),
            value: vehicleChargerPlugType,
            icon: () => IconComponent({ vehicleChargerPlugType }),
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label)),
    [t]
  );

  const selectedItem = useMemo(
    () =>
      vehicleChargerPlugTypesDropdownItems.find(
        (item) => item.value === selectedChargerPlugType
      ),
    [selectedChargerPlugType, vehicleChargerPlugTypesDropdownItems]
  );

  return (
    <Dropdown
      items={vehicleChargerPlugTypesDropdownItems}
      defaultItem={selectedItem}
      onSelect={(item) =>
        setSelectedChargerPlugType({
          appSettings: { evPlugType: item.value },
        })
      }
      placeholder={t('vehicle.form.chargerPlugType.label')}
      dropdownIcon={DropdownIcon}
      checkIcon={CheckIcon}
    />
  );
};

export default React.memo(VehicleChargerPlugTypeSelector);
